import { useState } from 'react';
import { Icon } from './icon/index.ts';
import styles from './Dropzone.module.css';

/**
 * No § 14 entry names Upload yet — this follows Input's own construction: a
 * real `<input type="file">` under a drawn box, exactly as `Checkbox` covers
 * its row, so the file picker, the keyboard and the announcement stay the
 * browser's own rather than reimplemented.
 *
 * A file dropped directly onto the input is the platform's own behaviour —
 * a browser sets a file input's `FileList` from a drop with no `DataTransfer`
 * handling required — so the one thing this component owns is the affordance
 * around a control the browser already accepts a drop on.
 *
 * The label lives in Field, which is where § 14 puts every field's label.
 */

interface DropzoneProps {
  id: string;
  accept?: string;
  disabled?: boolean;
  /** Set when Field is rendering an error for this control. */
  invalid?: boolean;
  describedBy?: string;
  /** The accessible name, where no visible `Field` label supplies one. */
  ariaLabel?: string;
  /** The instructional line inside the box. */
  prompt?: string;
  /** § 16 — the toolbar and table-row height, for a field inside a dense
      administrative form. */
  size?: 'default' | 'compact';
  onFiles: (files: FileList) => void;
}

export function Dropzone({
  id,
  accept,
  disabled = false,
  invalid = false,
  describedBy,
  ariaLabel,
  prompt = 'Arraste ou clique',
  size = 'default',
  onFiles,
}: DropzoneProps) {
  const [dragging, setDragging] = useState(false);

  const state = disabled ? 'disabled' : invalid ? 'error' : dragging ? 'drag' : 'default';

  return (
    <span className={styles.zone} data-state={state} data-size={size}>
      <input
        id={id}
        className={styles.input}
        type="file"
        accept={accept}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        onDragEnter={() => {
          if (!disabled) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={() => setDragging(false)}
        onChange={(event) => {
          if (event.target.files && event.target.files.length > 0) onFiles(event.target.files);
        }}
      />
      <span className={styles.content}>
        <Icon name="upload" size={size === 'compact' ? 16 : 20} />
        <span className={styles.prompt}>{prompt}</span>
      </span>
    </span>
  );
}
